import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { setObjectPreview } from '_common/components/ObjectPreview/ObjectPreviewSlice';
import { useGetUserSettingsQuery } from '_common/services/api/UserApi';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';
import { navigateToEditor, navigateToObject } from 'router/navigation';

import {
  ThunksTable,
  Cell,
  Footer,
  SelectionCounter,
  objectIsOverdue,
  type TableColumn,
  type TableRow,
  type onDoubleClickHandler,
  type rowHasErrorCallback,
} from '_common/components/Table';
import { UsernameLabel } from '_common/components';

import styles from '../SharedListPage.module.scss';

type TableProps = {
  fetchObjects: (listParams: Request.AllListParams) => Promise<Request.FetchResponse>;
};

const Table = ({ fetchObjects }: TableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const data = useSelector((state) => state.app.data);
  const list = useSelector((state) => state.table.identity.shared.list);
  const appLoading = useSelector((state) => state.app.loading.isOpen);

  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: metadata } = useGetMetadataListQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: result.data ?? {} }),
  });

  const veevaDocNumberId = Object.keys(metadata).find(
    (key) => metadata?.[key]?.trigger === 'veeva_doc_number',
  );
  const veevaGlobalId = Object.keys(metadata).find(
    (key) => metadata?.[key]?.trigger === 'veeva_global_id',
  );

  const columns = useMemo<TableColumn<Objekt, keyof TableCustomisation>[]>(() => {
    const columns: TableColumn<Objekt, keyof TableCustomisation>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 240,
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-name-column`} disabled={object.status === 'processing'}>
              <Cell.ObjectName object={object} testId={`${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'storage.browserHeader.status' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-status-column`} disabled={object.status === 'processing'}>
              <Cell.ObjectStatus object={object} testId="object-status" />
            </Cell>
          );
        },
      },

      {
        id: 'time.modification',
        accessorKey: 'time.modification',
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-date-modified-column`}
              disabled={object.status === 'processing'}
            >
              <Cell.DateTime
                date={{ date: object.time?.modification }}
                time={{ time: object.time?.modification }}
              />
            </Cell>
          );
        },
      },

      {
        id: 'shared_with',
        accessorKey: 'shared_with',
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.sharedWith' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-shared-column`} disabled={object.status === 'processing'}>
              <Cell.Collaborators object={object} />
            </Cell>
          );
        },
      },

      {
        id: 'owner',
        accessorKey: 'owner',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({ id: 'global.owner' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-owner-column`}
              ellipsis
              disabled={object.status === 'processing'}
            >
              <UsernameLabel userId={object.owner} />
            </Cell>
          );
        },
      },

      {
        id: 'time.creation',
        accessorKey: 'time.creation',
        size: 160,
        header: intl.formatMessage({ id: 'CREATION_DATE' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-creation-date-column`}
              disabled={object.status === 'processing'}
            >
              <Cell.DateTime
                date={{ date: object.time?.creation }}
                time={{ time: object.time?.creation }}
              />
            </Cell>
          );
        },
      },
    ];

    if (veevaDocNumberId) {
      columns.push({
        id: 'metadata.veeva_doc_number',
        accessorFn: (object) => (veevaDocNumberId ? object.metadata?.[veevaDocNumberId] : null),
        size: 160,
        header: intl.formatMessage({ id: 'VEEVA_DOC_NUMBER' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-veeva-doc-number`}
              disabled={object.status === 'processing'}
            >
              {veevaDocNumberId ? object?.metadata?.[veevaDocNumberId] : null}
            </Cell>
          );
        },
      });
    }

    if (veevaGlobalId) {
      columns.push({
        id: 'metadata.veeva_global_id',
        accessorFn: (object) => (veevaGlobalId ? object?.metadata?.[veevaGlobalId] : null),
        size: 160,
        header: intl.formatMessage({ id: 'VEEVA_GLOBAL_ID' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-veeva-global-id`} disabled={object.status === 'processing'}>
              {veevaGlobalId ? object?.metadata?.[veevaGlobalId] : null}
            </Cell>
          );
        },
      });
    }
    return columns.filter((column) => userSettings?.tables.explorer[column.id]);
  }, [userSettings]);

  const rowsData = useMemo(() => {
    return list?.map<TableRow<Objekt>>((objectId) => {
      return data[objectId];
    });
  }, [list, data]);

  const renderEmptyState = () => {
    if (!appLoading) {
      return (
        <div className={styles.emptyView}>
          <EmptyState
            size="l"
            icon="folder-user"
            title={intl.formatMessage({ id: 'shared.emptyView.title' })}
            description={intl.formatMessage({ id: 'shared.emptyView.subtitle' })}
            testId="shared-page-empty-list"
          />
        </div>
      );
    }
    return null;
  };

  //@ts-expect-error
  const handleRowDoubleClick: onDoubleClickHandler = (row, originalEvent) => {
    const currentObject = data[row.id];

    if (currentObject.status === 'processing' || currentObject.status === 'broken') {
      return;
    }

    switch (currentObject.type) {
      case 'document':
        navigateToEditor(currentObject.id);
        break;
      case 'file':
        dispatch(setObjectPreview({ id: currentObject.id }));
        originalEvent.preventDefault();
        break;
      case 'folder':
        navigateToObject('folder', currentObject.id);
        break;
      case 'dopdf':
        window.open(`/pdf/${currentObject.id}`, '_blank');
        break;
      case 'presentation':
        window.open(`/presentation/${currentObject.id}`, '_blank');
        break;
      default:
        break;
    }
  };

  const rowHasError: rowHasErrorCallback = (row) => {
    const object = data[row.id];
    return objectIsOverdue(object);
  };

  return (
    <ThunksTable
      identity={'shared'}
      options={{ columns, data: rowsData }}
      lazyLoading
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      renderFooter={() => (
        <Footer>
          <SelectionCounter identity={'shared'} />
        </Footer>
      )}
      renderEmptyState={renderEmptyState}
      fetchObjects={fetchObjects}
      onDoubleClick={handleRowDoubleClick}
      rowHasError={rowHasError}
      testId="shared-page"
    />
  );
};

export default Table;
